import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import Spinner from '@atlaskit/spinner';
import { DN800A } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { fontFamily, fontSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { EmailFormat } from '../../common/types';
import type { ContentPreviewType, ContentPreviewProps } from '../../common/types/content-preview';
import { useEmailTemplatePanelContentPreview } from '../../services/content-preview/index.tsx';
import messages from './messages';
import type { FrameProps } from './types';

export const ContentPreview: ContentPreviewType = ({
	content,
	initialData,
	projectKey,
	ruleSetId,
}: ContentPreviewProps) => {
	const previewDependenciesRef = useRef<{
		css?: string;
		format?: EmailFormat;
		htmlTemplate?: string;
		textTemplate: string;
	}>({
		htmlTemplate: '',
		css: '',
		format: '',
		textTemplate: '',
	});

	const iframeEl = useRef<HTMLIFrameElement>(null);
	const [height, setHeight] = useState(200);
	const { formatMessage } = useIntl();

	const {
		data: previewData,
		error: previewError,
		loading: previewLoading,
		fetch: fetchPreviewData,
	} = useEmailTemplatePanelContentPreview(content, initialData, projectKey, ruleSetId);

	useEffect(() => {
		const hasChanges =
			previewDependenciesRef.current.css !== content.css ||
			previewDependenciesRef.current.htmlTemplate !== content.htmlTemplate ||
			previewDependenciesRef.current.format !== initialData.format ||
			previewDependenciesRef.current.textTemplate !== content.textTemplate;

		if (!previewLoading && hasChanges) {
			previewDependenciesRef.current = {
				htmlTemplate: content.htmlTemplate,
				css: content.css,
				format: initialData.format,
				textTemplate: content.textTemplate,
			};
			fetchPreviewData();
		}
	}, [
		fetchPreviewData,
		previewData,
		previewLoading,
		initialData.format,
		content.css,
		content.htmlTemplate,
		content.textTemplate,
	]);

	const handleResize = () => {
		const bodyEl = iframeEl.current?.contentWindow?.document?.body;
		if (bodyEl && bodyEl?.scrollHeight) {
			setHeight(bodyEl?.scrollHeight);
		}
	};

	useEffect(() => {
		const contentDocument = iframeEl?.current?.contentDocument;
		if (previewData?.renderedPreview && contentDocument) {
			contentDocument.open('text/html', 'replace');
			contentDocument.write(previewData?.renderedPreview);
			contentDocument.close();

			const anchorTags = contentDocument.getElementsByTagName('a');
			for (let i = 0; i < anchorTags.length; i++) {
				anchorTags[i].removeAttribute('href');
			}
			handleResize();
		}
	}, [previewData]);

	if (previewLoading) {
		return <Spinner label={formatMessage(messages.spinnerLabel)} />;
	}

	if (previewError) {
		return (
			<PreviewError>
				<iframe
					title="language-support.content.previewError"
					content={`Error loading preview: ${previewError.message}`}
				/>
			</PreviewError>
		);
	}

	return (
		<IFrameWrapper
			height={height}
			data-testid="language-settings.ui.content-preview.i-frame-wrapper"
		>
			<iframe title="language-support.content.preview" sandbox="allow-same-origin" ref={iframeEl} />
		</IFrameWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IFrameWrapper = styled.div<{
	height: number;
	['data-testid']: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	iframe: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: `${(props: FrameProps) => props.height + 32}px`,
		margin: 0,
		padding: 0,
		borderColor: token('color.border.input', DN800A),
		boxSizing: 'border-box',
		pointerEvents: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	html: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		fontFamily: `${fontFamily}, sans-serif`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		fontSize: `${fontSize}px`,
	},
	paddingBottom: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewError = styled.div({
	color: token('color.background.danger', 'colors.R800'),
	fontWeight: 'bold',
	padding: token('space.100', '8px'),
});
